import { ref as storageRef, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { callable } from './useEntitlement';
import { attachmentEntry, composeWord, messageAttachments } from '../domain/compose';
import {
  FileRefused, UPLOAD_FAILED, uploadErrorMessage, uploadType,
} from '../lib/fileLimits';
import { UploadCancelled, watchUpload } from '../lib/uploadWatch';

// Attach Files' uploads (#605): a picked file goes to Storage at once, under
// the message it is for, and waits there until the message is sent.
//
// `attachments/{uid}/{messageId}/{cap}` — `authorizeUpload` (functions/
// index.js, `purpose: 'attachment'`) mints the cap and the path, spends one of
// the studio's daily uploads and checks its storage ceiling, as every upload
// does; storage.rules lets the object be written once, up to 20 MB, against
// that reservation, and lets the teacher delete it. Nothing reads it but the
// sender. core/src/compose.rs has MMS's reading and the bounds; the composer's
// state is src/hooks/useNewMessage.js, which is handed these two functions so
// it holds no Firebase.
//
// **Who deletes the bytes.** `discardAttachment` here, at once, when the
// teacher removes a file from the message or closes the composer without
// sending — best-effort, because the sweep (functions/retention/
// attachments.js) finds whatever this misses: a file no message names, a
// week after it was uploaded, and a sent message's a week after it went.
// A message withdrawn with Cancel Scheduled Send leaves its files to the
// sweep on purpose: the withdrawal is a queued delete the rules may still
// refuse — the sender took the message first — and deleting the files
// beside it would send that message without them.

// A grant for anything but an attachment means the deployed server predates
// #605: its `authorizeUpload` reads the purpose as a library file and hands
// back a `files/` path, and its sender would send the message without the
// file. Refused with a sentence rather than uploaded into the library. The
// sentence is the core's, which both phones say too (#611).
const notYet = () => composeWord('attach_not_yet');

/**
 * Upload one picked file for message `messageId`: resolves `{ cap, name,
 * path, entry, size }` — `entry` the `{cap}/{name}` the message names it by —
 * or rejects with a `FileRefused` whose message is a sentence for the
 * teacher. `onProgress({ transferred, total })` as the bytes go up. The bytes
 * go up under the no-progress watchdog (src/lib/uploadWatch.js, #612).
 * Aborting `signal` cancels the upload and rejects with `UploadCancelled`;
 * bytes that landed anyway are the composer's to discard, as for a file
 * removed while it went up.
 */
export async function uploadAttachment({
  messageId, file, onProgress = null, signal = null,
}) {
  if (!storage) throw new FileRefused(UPLOAD_FAILED);
  const authorizeUpload = callable('authorizeUpload');
  if (!authorizeUpload) throw new FileRefused(notYet());

  // The type the object goes up as, and the one the callable is asked about:
  // the browser's, or the one its extension names, or — a type nobody named —
  // the generic one, since an attachment may be any type (MMS's chooser
  // restricts none).
  const contentType = uploadType(file) || 'application/octet-stream';

  let grant;
  try {
    const res = await authorizeUpload({
      purpose: 'attachment', messageId, name: file?.name || '', contentType, size: file?.size,
    });
    grant = res?.data;
  } catch (err) {
    throw new FileRefused(uploadErrorMessage(err));
  }
  if (grant?.purpose !== 'attachment' || !String(grant?.path || '').startsWith('attachments/')) {
    throw new FileRefused(notYet());
  }
  const entry = attachmentEntry(grant.cap, grant.name || file?.name || '');
  if (!entry) throw new FileRefused(UPLOAD_FAILED);

  // Cancelled while the server was asked: nothing went up, so nothing to say.
  if (signal?.aborted) throw new UploadCancelled();
  const task = uploadBytesResumable(storageRef(storage, grant.path), file, { contentType });
  await watchUpload(task, { total: file?.size || 0, onProgress, signal });

  const [{ cap, name }] = messageAttachments([entry]);
  return {
    cap, name, path: grant.path, entry, size: file?.size || 0,
  };
}

/**
 * Delete an uploaded file the message will not carry. Best-effort: resolves
 * either way, and the sweep removes what this could not.
 */
export function discardAttachment(path) {
  if (!storage || !path) return Promise.resolve(false);
  return deleteObject(storageRef(storage, path)).then(
    () => true,
    (err) => {
      console.warn('[attachments] an unsent file was not deleted; the sweep will remove it', err);
      return false;
    },
  );
}
